var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.uri.selector",
  title: "URI Selector Control",
  description: "URI input with permission-aware file picker, multi-select, and third-party URL support.",
  control: true,
  supportServer: false,
  fields: {
    name: { title: "Name", type: "string", description: "Control name.", required: true },
    title: { title: "Title", type: "string", description: "Control title.", required: true },
    value: { title: "Value", type: ["string", "list"], description: "Current URI value." },
    required: { title: "Required", type: "boolean", description: "Whether URI is required.", default: false },
    multiselect: { title: "Multi Select", type: "boolean", description: "Allow selecting multiple URIs.", default: false },
    allowupload: { title: "Allow Upload", type: "boolean", description: "Allow upload in picker when user has permission.", default: true },
    allowthirdparty: { title: "Allow Third Party", type: "boolean", description: "Show Third Party root in picker for external URLs.", default: true },
    expectedkind: { title: "Expected Kind", type: ["string", "list"], description: "Optional media kind filter: image, video, audio, document, other." },
    protocols: { title: "Allowed Protocols", type: "list", of: "string", description: "Allowed URI protocols." },
    allowedProtocols: { title: "Allowed Protocols Alias", type: "list", of: "string", description: "Alias for protocols." },
    url: { title: "URL Only", type: "boolean", description: "Must be http/https.", default: false },
    securedUrl: { title: "Secured URL", type: "boolean", description: "Must be https.", default: false },
    filePath: { title: "File Path", type: "boolean", description: "Must be a file path.", default: false },
    allowInternalContent: { title: "Allow Internal Content", type: "boolean", description: "Allow /content/* and /secured/*.", default: true },
    allowProviderUri: { title: "Allow Provider URI", type: "boolean", description: "Allow dropbox://, gdrive://, s3://, ftp://, provider://.", default: true },
    "picker-title": { title: "Picker Title", type: "string", description: "Optional full-screen picker title." },
    "picker-description": { title: "Picker Description", type: "string", description: "Optional full-screen picker description." }
  }
});

var jsonRender = require("../interface/jsonRender.js");
var uai = require("../interface/interface.js");

var STYLE_ID = "ua-uri-selector-styles";
var THIRD_PARTY_ROOT = "__third_party__";

function safe(value) {
  return (value || "").toString();
}

function lower(value) {
  return safe(value).trim().toLowerCase();
}

function esc(value) {
  return safe(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function bool(options, key, fallback) {
  if (!(key in options)) return fallback;
  return options[key] === true || options[key] === "true";
}

function isTrue(value) {
  return value === true || value === "true";
}

function isFalse(value) {
  return value === false || value === "false";
}

function toArray(value) {
  if (!(value)) return [];
  if (value instanceof Array) return value.slice();
  return [value];
}

function formatBytes(value) {
  var bytes = Math.max(0, Number(value || 0));
  var units = ["B", "KB", "MB", "GB", "TB"];
  var idx = 0;
  while (bytes >= 1024 && idx < units.length - 1) {
    bytes = bytes / 1024;
    idx += 1;
  }
  return (idx === 0 ? Math.round(bytes) : bytes.toFixed(1)) + " " + units[idx];
}

function normalizeFolderPath(value) {
  var raw = safe(value).trim().replace(/\\/g, "/");
  raw = raw.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!(raw)) return "";

  var parts = raw.split("/").filter(Boolean);
  var safeParts = [];
  for (var i = 0; i < parts.length; i++) {
    if (parts[i] === "." || parts[i] === "..") continue;
    safeParts.push(parts[i].replace(/[^a-zA-Z0-9._-]/g, ""));
  }

  return safeParts.filter(Boolean).join("/");
}

function folderLabel(pathValue) {
  var cleaned = normalizeFolderPath(pathValue || "");
  if (!(cleaned)) return "Root";
  var parts = cleaned.split("/");
  return parts[parts.length - 1] || cleaned;
}

function parseExpectedKinds(value) {
  var values = toArray(value).map(function(item) {
    return lower(item);
  }).filter(Boolean);
  if (values.length === 0) return [];
  return values;
}

function parseProtocols(options) {
  var values = [];
  var listA = toArray(options.protocols || []);
  var listB = toArray(options.allowedProtocols || []);

  for (var i = 0; i < listA.length; i++) values.push(lower(listA[i]));
  for (var j = 0; j < listB.length; j++) values.push(lower(listB[j]));

  return values.filter(Boolean).filter(function(item, index, arr) {
    return arr.indexOf(item) === index;
  });
}

function resolveProviderUri(uri) {
  var raw = safe(uri).trim();
  if (!(raw)) return "";
  if (raw.match(/^https?:\/\//i)) return raw;

  if (raw.match(/^dropbox:\/\//i)) {
    var payload = raw.replace(/^dropbox:\/\//i, "");
    if (payload.match(/^https?:\/\//i)) return payload;
    if (payload.startsWith("s/")) return "https://www.dropbox.com/" + payload;
    return "https://www.dropbox.com/home/" + payload.replace(/^\/+/, "");
  }

  if (raw.match(/^gdrive:\/\//i)) {
    var g = raw.replace(/^gdrive:\/\//i, "");
    if (g.match(/^https?:\/\//i)) return g;
    if (g.match(/^[a-zA-Z0-9_-]{10,}$/)) return "https://drive.google.com/file/d/" + g + "/view";
    return "https://drive.google.com/drive/my-drive";
  }

  if (raw.match(/^s3:\/\//i)) {
    var s3 = raw.replace(/^s3:\/\//i, "");
    var slash = s3.indexOf("/");
    if (slash > 0) {
      var bucket = s3.slice(0, slash);
      var key = s3.slice(slash + 1);
      return "https://" + bucket + ".s3.amazonaws.com/" + key;
    }
  }

  if (raw.match(/^provider:\/\//i)) return raw.replace(/^provider:\/\//i, "https://");
  return raw;
}

function parseValues(input, multiselect) {
  if (multiselect) {
    var values = [];
    var list = toArray(input);
    for (var i = 0; i < list.length; i++) {
      var item = safe(list[i]);
      if (item.indexOf("\n") >= 0) {
        var lines = item.split("\n");
        for (var l = 0; l < lines.length; l++) list.push(lines[l]);
        continue;
      }
      if (item.indexOf(",") >= 0) {
        var parts = item.split(",");
        for (var p = 0; p < parts.length; p++) list.push(parts[p]);
        continue;
      }
      item = item.trim();
      if (!(item)) continue;
      values.push(item);
    }
    return values.filter(function(item, index, arr) {
      return arr.indexOf(item) === index;
    });
  }

  if (input instanceof Array) {
    return input.length > 0 ? [safe(input[0]).trim()] : [];
  }

  var text = safe(input).trim();
  return text ? [text] : [];
}

function uriValidationError(uri, options) {
  var value = safe(uri).trim();
  if (!(value)) return "A URI value is required.";

  var uriPattern = /^(https?:\/\/|file:\/\/|@!\/|\.{1,2}\/|\/|mailto:|ftp:\/\/|sftp:\/\/|s3:\/\/|dropbox:\/\/|gdrive:\/\/|provider:\/\/)[^\s]+$/i;
  if (!uriPattern.test(value)) {
    return "The value is not a supported URI format.";
  }

  var protocols = parseProtocols(options);
  if (protocols.length > 0) {
    var protocolMatch = value.match(/^([a-zA-Z][a-zA-Z0-9+\-.]*):/);
    if (protocolMatch) {
      var protocol = lower(protocolMatch[1]);
      if (protocols.indexOf(protocol) < 0) {
        return "Protocol '" + protocol + "' is not allowed.";
      }
    }
  }

  if (isTrue(options.url)) {
    if (!(value.match(/^https?:\/\//i))) return "The value must be an http(s) URL.";
  }

  if (isTrue(options.securedUrl)) {
    if (!(value.match(/^https:\/\//i))) return "The value must be an https URL.";
  }

  if (isTrue(options.filePath)) {
    if (!(value.match(/^(?:\.{1,2}\/|\/|@!\/)[\w\-./@!]+$/))) {
      return "The value must be a valid file path.";
    }
  }

  if (isFalse(options.allowInternalContent)) {
    if (value.indexOf("/content/") === 0 || value.indexOf("/secured/") === 0) {
      return "Internal content URIs are disabled for this field.";
    }
  }

  if (isFalse(options.allowProviderUri)) {
    if (value.match(/^(dropbox|gdrive|provider|ftp|s3|sftp):\/{0,2}/i)) {
      return "Provider URIs are disabled for this field.";
    }
  }

  return "";
}

async function postJson(url, payload) {
  var response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {})
  });

  var body = null;
  try {
    body = await response.json();
  } catch (error) {
    throw new Error("The server returned an invalid JSON response.");
  }

  if (!(response.ok) || !(body && body.success)) {
    var err = ((body && body.errors && body.errors[0]) || body.error || {});
    var message = err.description || err.title || "Request failed.";
    var requestError = new Error(message);
    requestError.error = err;
    throw requestError;
  }

  return body.data || {};
}

function ensureStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;

  var style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = [
    ".ua-uri-picker-overlay{position:fixed;inset:0;z-index:12000;background:rgba(15,23,42,.82);display:flex;align-items:stretch;justify-content:center;}",
    ".ua-uri-picker-shell{background:#fff;width:100%;height:100%;display:flex;flex-direction:column;}",
    ".ua-uri-picker-header{padding:1rem 1.25rem;border-bottom:1px solid #e5e7eb;display:flex;justify-content:space-between;gap:.75rem;align-items:flex-start;}",
    ".ua-uri-picker-title{font-size:1.1rem;font-weight:600;margin:0;}",
    ".ua-uri-picker-desc{margin:.25rem 0 0 0;color:#6b7280;font-size:.9rem;}",
    ".ua-uri-picker-body{flex:1;min-height:0;display:grid;grid-template-columns:minmax(14rem,20rem) 1fr;}",
    ".ua-uri-picker-sidebar{border-right:1px solid #e5e7eb;overflow:auto;padding:.75rem;background:#f8fafc;}",
    ".ua-uri-picker-main{display:flex;flex-direction:column;min-width:0;}",
    ".ua-uri-picker-toolbar{padding:.75rem 1rem;border-bottom:1px solid #e5e7eb;display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;}",
    ".ua-uri-picker-crumbs{padding:.55rem 1rem;border-bottom:1px solid #f1f5f9;font-size:.88rem;display:flex;gap:.4rem;flex-wrap:wrap;}",
    ".ua-uri-picker-crumb{border:none;background:transparent;color:#0f766e;padding:0;}",
    ".ua-uri-picker-content{flex:1;min-height:0;overflow:auto;padding:.9rem 1rem;}",
    ".ua-uri-picker-files{display:flex;flex-direction:column;gap:.55rem;}",
    ".ua-uri-picker-file{border:1px solid #e5e7eb;border-radius:.65rem;padding:.7rem;display:flex;justify-content:space-between;gap:.75rem;cursor:pointer;}",
    ".ua-uri-picker-file.active{border-color:#0d6efd;background:#eff6ff;}",
    ".ua-uri-picker-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(12rem,1fr));gap:.75rem;}",
    ".ua-uri-picker-card{border:1px solid #e5e7eb;border-radius:.75rem;padding:.7rem;cursor:pointer;display:flex;flex-direction:column;gap:.35rem;}",
    ".ua-uri-picker-card.active{border-color:#0d6efd;background:#eff6ff;}",
    ".ua-uri-picker-footer{padding:.75rem 1rem;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;gap:.75rem;align-items:center;}",
    ".ua-uri-picker-folder{width:100%;text-align:left;border:1px solid transparent;background:transparent;padding:.35rem .5rem;border-radius:.5rem;font-size:.9rem;}",
    ".ua-uri-picker-folder.active{background:#e2e8f0;border-color:#cbd5e1;}",
    ".ua-uri-selector-values{display:flex;gap:.4rem;flex-wrap:wrap;margin-top:.5rem;}",
    ".ua-uri-selector-value{display:inline-flex;align-items:center;gap:.25rem;border:1px solid #d1d5db;border-radius:9999rem;padding:.2rem .55rem;font-size:.8rem;background:#f8fafc;}",
    ".ua-uri-picker-inline-status{font-size:.85rem;color:#6b7280;}",
    ".ua-uri-picker-empty{padding:1.2rem;border:1px dashed #d1d5db;border-radius:.7rem;color:#6b7280;background:#f9fafb;text-align:center;}",
    "@media (max-width: 64rem){.ua-uri-picker-body{grid-template-columns:1fr;}.ua-uri-picker-sidebar{max-height:10rem;border-right:none;border-bottom:1px solid #e5e7eb;}}"
  ].join("\n");

  document.head.appendChild(style);
}

function renderValuePills(host, values, onRemove) {
  if (!(host)) return;
  host.innerHTML = "";
  var rows = toArray(values || []);
  for (var i = 0; i < rows.length; i++) {
    var pill = document.createElement("span");
    pill.className = "ua-uri-selector-value";
    pill.innerHTML = "<span>" + esc(rows[i]) + "</span>";

    var remove = document.createElement("button");
    remove.type = "button";
    remove.className = "btn btn-sm p-0 border-0";
    remove.innerHTML = "<i class='bi bi-x'></i>";
    remove.addEventListener("click", (function(uri) {
      return function() {
        onRemove(uri);
      };
    })(rows[i]));

    pill.appendChild(remove);
    host.appendChild(pill);
  }
}

function assignState(control, values, multiselect, options) {
  var list = parseValues(values || [], multiselect).map(function(item) {
    return safe(item).trim();
  }).filter(Boolean);
  control._uaUriValues = list;
  control._uaUriOptions = options || control._uaUriOptions || {};
}

function readState(control, input, multiselect) {
  var stateValues = toArray(control._uaUriValues || []);
  if (stateValues.length > 0) return parseValues(stateValues, multiselect);
  return parseValues(input.value || "", multiselect);
}

async function openPicker(control, options, state) {
  ensureStyles();

  var title = safe(options["picker-title"] || options.title || "Select URI").trim() || "Select URI";
  var description = safe(options["picker-description"] || "Choose from uploaded files or add external URLs.").trim();
  var multiselect = state.multiselect;
  var expectedKinds = state.expectedKinds;

  var selected = {};
  for (var i = 0; i < state.values.length; i++) {
    selected[state.values[i]] = {
      uri: state.values[i],
      title: state.values[i],
      scope: "",
      mediaKind: "other"
    };
  }

  var pickerState = {
    currentFolder: "",
    folders: [],
    breadcrumbs: [],
    rows: [],
    viewMode: "list",
    search: "",
    kindFilter: expectedKinds.length === 1 ? expectedKinds[0] : "",
    permissions: { canUpload: false, canCreateFolder: false, ownerScoped: false },
    ownerFolder: "",
    uploadHost: null,
    selected: selected,
    allowThirdParty: state.allowThirdParty === true
  };

  var overlay = document.createElement("div");
  overlay.className = "ua-uri-picker-overlay";

  overlay.innerHTML = ""
    + "<div class='ua-uri-picker-shell'>"
    + "  <div class='ua-uri-picker-header'>"
    + "    <div>"
    + "      <h2 class='ua-uri-picker-title'>" + esc(title) + "</h2>"
    + "      <p class='ua-uri-picker-desc'>" + esc(description) + "</p>"
    + "    </div>"
    + "    <button type='button' class='btn btn-outline-secondary btn-sm' data-uri-close='true'><i class='bi bi-x-lg'></i></button>"
    + "  </div>"
    + "  <div class='ua-uri-picker-body'>"
    + "    <div class='ua-uri-picker-sidebar' data-uri-sidebar='true'></div>"
    + "    <div class='ua-uri-picker-main'>"
    + "      <div class='ua-uri-picker-toolbar'>"
    + "        <input type='search' class='form-control form-control-sm' style='max-width:18rem;' placeholder='Search files...' data-uri-search='true' />"
    + "        <select class='form-select form-select-sm' style='max-width:12rem;' data-uri-kind='true'>"
    + "          <option value=''>All Types</option>"
    + "          <option value='image'>Images</option>"
    + "          <option value='video'>Videos</option>"
    + "          <option value='audio'>Audio</option>"
    + "          <option value='document'>Documents</option>"
    + "          <option value='other'>Other</option>"
    + "        </select>"
    + "        <div class='btn-group btn-group-sm' role='group'>"
    + "          <button type='button' class='btn btn-outline-secondary active' data-uri-view='list'><i class='bi bi-list'></i></button>"
    + "          <button type='button' class='btn btn-outline-secondary' data-uri-view='gallery'><i class='bi bi-grid'></i></button>"
    + "        </div>"
    + "        <button type='button' class='btn btn-outline-primary btn-sm ms-auto d-none' data-uri-new-folder='true'><i class='bi bi-folder-plus'></i></button>"
    + "      </div>"
    + "      <div class='ua-uri-picker-crumbs' data-uri-crumbs='true'></div>"
    + "      <div class='px-3 py-2 d-none' data-uri-upload='true'></div>"
    + "      <div class='ua-uri-picker-content' data-uri-content='true'></div>"
    + "      <div class='ua-uri-picker-footer'>"
    + "        <div class='ua-uri-picker-inline-status' data-uri-status='true'></div>"
    + "        <div class='d-flex gap-2'>"
    + "          <button type='button' class='btn btn-outline-secondary btn-sm' data-uri-cancel='true'>Cancel</button>"
    + "          <button type='button' class='btn btn-primary btn-sm' data-uri-apply='true' disabled>Use Selected</button>"
    + "        </div>"
    + "      </div>"
    + "    </div>"
    + "  </div>"
    + "</div>";

  document.body.appendChild(overlay);

  var sidebar = overlay.querySelector("[data-uri-sidebar]");
  var crumbs = overlay.querySelector("[data-uri-crumbs]");
  var content = overlay.querySelector("[data-uri-content]");
  var searchInput = overlay.querySelector("[data-uri-search]");
  var kindInput = overlay.querySelector("[data-uri-kind]");
  var newFolderBtn = overlay.querySelector("[data-uri-new-folder]");
  var uploadHost = overlay.querySelector("[data-uri-upload]");
  var applyBtn = overlay.querySelector("[data-uri-apply]");
  var statusHost = overlay.querySelector("[data-uri-status]");

  kindInput.value = pickerState.kindFilter;

  function close() {
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
  }

  function syncStatus(message, isError) {
    if (!(statusHost)) return;
    statusHost.textContent = safe(message || "");
    statusHost.style.color = isError ? "#b91c1c" : "#6b7280";
  }

  function selectedValues() {
    return Object.keys(pickerState.selected);
  }

  function toggleSelected(row) {
    var uri = safe(row.uri || "").trim();
    if (!(uri)) return;

    if (pickerState.selected[uri]) {
      delete pickerState.selected[uri];
    } else {
      if (!(multiselect)) pickerState.selected = {};
      pickerState.selected[uri] = {
        uri: uri,
        title: row.title || row.originalName || uri,
        scope: row.scope || "",
        mediaKind: row.mediaKind || "other"
      };
    }

    renderRows();
    updateApplyState();
  }

  function updateApplyState() {
    var count = selectedValues().length;
    applyBtn.disabled = count === 0;
    applyBtn.textContent = count > 0
      ? (multiselect ? ("Use Selected (" + count + ")") : "Use Selected")
      : "Use Selected";
  }

  function isSelectedUri(uri) {
    return Object.prototype.hasOwnProperty.call(pickerState.selected, safe(uri).trim());
  }

  function renderBreadcrumbs() {
    if (!(crumbs)) return;
    crumbs.innerHTML = "";

    var rootBtn = document.createElement("button");
    rootBtn.type = "button";
    rootBtn.className = "ua-uri-picker-crumb";
    rootBtn.innerHTML = "<i class='bi bi-house-door'></i> Root";
    rootBtn.addEventListener("click", function() {
      pickerState.currentFolder = "";
      loadData();
    });
    crumbs.appendChild(rootBtn);

    if (pickerState.currentFolder === THIRD_PARTY_ROOT) {
      var third = document.createElement("button");
      third.type = "button";
      third.className = "ua-uri-picker-crumb";
      third.textContent = "Third Party";
      crumbs.appendChild(document.createTextNode(" / "));
      crumbs.appendChild(third);
      return;
    }

    var breadcrumbs = pickerState.breadcrumbs instanceof Array ? pickerState.breadcrumbs : [];
    for (var i = 0; i < breadcrumbs.length; i++) {
      crumbs.appendChild(document.createTextNode(" / "));
      var item = document.createElement("button");
      item.type = "button";
      item.className = "ua-uri-picker-crumb";
      item.textContent = safe(breadcrumbs[i].label || folderLabel(breadcrumbs[i].path || ""));
      item.addEventListener("click", (function(pathValue) {
        return function() {
          pickerState.currentFolder = normalizeFolderPath(pathValue || "");
          loadData();
        };
      })(breadcrumbs[i].path || ""));
      crumbs.appendChild(item);
    }
  }

  function renderFolders() {
    if (!(sidebar)) return;
    sidebar.innerHTML = "";

    var folders = pickerState.folders instanceof Array ? pickerState.folders : [];
    for (var i = 0; i < folders.length; i++) {
      var row = folders[i] || {};
      var pathValue = normalizeFolderPath(row.path || "");
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ua-uri-picker-folder" + (pathValue === pickerState.currentFolder ? " active" : "");
      btn.style.paddingLeft = (0.5 + ((Number(row.depth || 0)) * 0.9)) + "rem";
      btn.innerHTML = "<i class='bi " + (pathValue ? "bi-folder" : "bi-house-door") + " me-1'></i>" + esc(pathValue ? (row.label || folderLabel(pathValue)) : "Root");
      btn.addEventListener("click", (function(path) {
        return function() {
          pickerState.currentFolder = path;
          loadData();
        };
      })(pathValue));
      sidebar.appendChild(btn);
    }

    if (pickerState.allowThirdParty) {
      var thirdBtn = document.createElement("button");
      thirdBtn.type = "button";
      thirdBtn.className = "ua-uri-picker-folder" + (pickerState.currentFolder === THIRD_PARTY_ROOT ? " active" : "");
      thirdBtn.innerHTML = "<i class='bi bi-link-45deg me-1'></i>Third Party";
      thirdBtn.addEventListener("click", function() {
        pickerState.currentFolder = THIRD_PARTY_ROOT;
        renderBreadcrumbs();
        renderUpload();
        renderRows();
      });
      sidebar.appendChild(thirdBtn);
    }
  }

  function renderThirdPartyPanel() {
    content.innerHTML = "";

    var shell = document.createElement("div");
    shell.className = "d-flex flex-column gap-2";

    var prompt = document.createElement("div");
    prompt.className = "small text-muted";
    prompt.textContent = "Add external/provider URLs for videos, embeds, and remote assets.";
    shell.appendChild(prompt);

    var row = document.createElement("div");
    row.className = "input-group";
    row.innerHTML = "<input type='text' class='form-control form-control-sm' placeholder='https://example.com/media or provider://...'><button type='button' class='btn btn-outline-primary btn-sm'>Add URL</button>";
    var input = row.querySelector("input");
    var addBtn = row.querySelector("button");

    addBtn.addEventListener("click", function() {
      var raw = safe(input.value).trim();
      if (!(raw)) return;

      var normalized = resolveProviderUri(raw);
      var err = uriValidationError(normalized, options);
      if (err) {
        syncStatus(err, true);
        return;
      }

      if (!(multiselect)) pickerState.selected = {};
      pickerState.selected[normalized] = { uri: normalized, title: normalized, mediaKind: "other", scope: "external" };
      input.value = "";
      renderRows();
      updateApplyState();
      syncStatus("URL added.", false);
    });

    shell.appendChild(row);

    var rows = selectedValues().filter(function(uri) {
      return !(uri.indexOf("/content/") === 0 || uri.indexOf("/secured/") === 0);
    });

    if (rows.length === 0) {
      var empty = document.createElement("div");
      empty.className = "ua-uri-picker-empty";
      empty.textContent = "No external URLs selected yet.";
      shell.appendChild(empty);
    } else {
      var list = document.createElement("div");
      list.className = "ua-uri-picker-files";
      for (var i = 0; i < rows.length; i++) {
        var item = document.createElement("div");
        item.className = "ua-uri-picker-file active";
        item.innerHTML = "<div><div class='fw-semibold'>" + esc(rows[i]) + "</div><div class='small text-muted'>External URI</div></div><button type='button' class='btn btn-sm btn-outline-danger'><i class='bi bi-x'></i></button>";
        var removeBtn = item.querySelector("button");
        removeBtn.addEventListener("click", (function(uri) {
          return function() {
            delete pickerState.selected[uri];
            renderRows();
            updateApplyState();
          };
        })(rows[i]));
        list.appendChild(item);
      }
      shell.appendChild(list);
    }

    content.appendChild(shell);
  }

  function renderRows() {
    if (!(content)) return;

    if (pickerState.currentFolder === THIRD_PARTY_ROOT) {
      renderThirdPartyPanel();
      return;
    }

    content.innerHTML = "";

    var rows = pickerState.rows instanceof Array ? pickerState.rows : [];
    if (rows.length === 0) {
      var empty = document.createElement("div");
      empty.className = "ua-uri-picker-empty";
      empty.textContent = "No files found in this folder.";
      content.appendChild(empty);
      return;
    }

    var list = document.createElement("div");
    list.className = pickerState.viewMode === "gallery" ? "ua-uri-picker-gallery" : "ua-uri-picker-files";

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i] || {};
      var uri = safe(row.uri || "").trim();
      if (!(uri)) continue;

      var selected = isSelectedUri(uri);
      var item = document.createElement("div");

      if (pickerState.viewMode === "gallery") {
        item.className = "ua-uri-picker-card" + (selected ? " active" : "");
        item.innerHTML = ""
          + "<div class='fw-semibold'>" + esc(row.title || row.originalName || uri) + "</div>"
          + "<div class='small text-muted'>" + esc((row.mediaKind || "other") + " • " + formatBytes(row.size || 0)) + "</div>"
          + "<div class='small text-muted text-truncate'>" + esc(uri) + "</div>";
      } else {
        item.className = "ua-uri-picker-file" + (selected ? " active" : "");
        item.innerHTML = ""
          + "<div>"
          + "  <div class='fw-semibold'>" + esc(row.title || row.originalName || uri) + "</div>"
          + "  <div class='small text-muted'>" + esc((row.mediaKind || "other") + " • " + formatBytes(row.size || 0)) + " • " + esc(row.scope || "") + "</div>"
          + "  <div class='small text-muted text-truncate'>" + esc(uri) + "</div>"
          + "</div>"
          + "<div>" + (selected ? "<i class='bi bi-check2-circle text-primary'></i>" : "<i class='bi bi-circle text-muted'></i>") + "</div>";
      }

      item.addEventListener("click", (function(record) {
        return function() {
          toggleSelected(record);
        };
      })(row));

      list.appendChild(item);
    }

    content.appendChild(list);
  }

  async function renderUpload() {
    if (!(uploadHost)) return;

      var showUpload = (pickerState.permissions.canUpload === true)
      && state.allowUpload
      && (pickerState.currentFolder !== THIRD_PARTY_ROOT)
      && !(isFalse(options.allowInternalContent))
      && (window.UATools && typeof window.UATools.render === "function");

    uploadHost.classList.toggle("d-none", !showUpload);
    if (!showUpload) {
      uploadHost.innerHTML = "";
      pickerState.uploadHost = null;
      return;
    }

    if (pickerState.uploadHost && pickerState.uploadHost.parentNode === uploadHost) {
      pickerState.uploadHost.dispatchEvent(new CustomEvent("ua:fileupload:setfolder", {
        bubbles: true,
        detail: { folder: normalizeFolderPath(pickerState.currentFolder || "") }
      }));
      return;
    }

    uploadHost.innerHTML = "";
    var uploaderName = "uri-selector-upload-" + uai.generateRandomID(6);
    var uploaderJson = {
      n: "ua.file.upload",
      name: uploaderName,
      scope: "content",
      folder: normalizeFolderPath(pickerState.currentFolder || ""),
      "allow-scope-toggle": false,
      "allow-folder": false,
      allowmultiplefiles: true,
      allowfolders: true,
      allowdragdropfiles: true,
      allowwindowdropfiles: false,
      continuouswork: true,
      "show-preview": true
    };

    var uploaderEl = window.UATools.render(uploaderJson);
    if (!(uploaderEl)) return;

    uploadHost.appendChild(uploaderEl);
    pickerState.uploadHost = uploaderEl;

    uploaderEl.addEventListener("ua:fileupload:complete", function() {
      loadData();
    });

    uploaderEl.addEventListener("ua:fileupload:batchcomplete", function() {
      loadData();
    });
  }

  async function loadData() {
    try {
      renderBreadcrumbs();
      syncStatus("Loading...", false);

      if (pickerState.currentFolder === THIRD_PARTY_ROOT) {
        pickerState.rows = [];
        pickerState.breadcrumbs = [];
        renderFolders();
        renderUpload();
        renderRows();
        syncStatus("", false);
        updateApplyState();
        return;
      }

      var payload = {
        filters: {
          folder: normalizeFolderPath(pickerState.currentFolder || ""),
          search: safe(pickerState.search || ""),
          kind: safe(pickerState.kindFilter || "")
        },
        allowedKinds: expectedKinds
      };

      var data = await postJson("/files/picker/list", payload);
      pickerState.currentFolder = normalizeFolderPath(data.currentFolder || pickerState.currentFolder || "");
      pickerState.ownerFolder = normalizeFolderPath(data.ownerFolder || "");
      pickerState.folders = data.folders || [];
      pickerState.breadcrumbs = data.breadcrumbs || [];
      pickerState.rows = data.rows || [];
      pickerState.permissions = data.permissions || pickerState.permissions;

      renderFolders();
      renderBreadcrumbs();
      renderUpload();
      renderRows();
      syncStatus("", false);
      updateApplyState();
    } catch (error) {
      renderFolders();
      renderBreadcrumbs();
      renderUpload();
      syncStatus((error && error.message) || "Could not load files.", true);
      content.innerHTML = "<div class='ua-uri-picker-empty'>" + esc((error && error.message) || "Could not load files.") + "</div>";
    }
  }

  async function createFolder() {
    if (!(pickerState.permissions.canCreateFolder === true)) return;
    if (pickerState.currentFolder === THIRD_PARTY_ROOT) return;

    var folderName = await (async function askFolderName() {
      return await new Promise(function(resolve) {
        var dialog = document.createElement("div");
        dialog.style.position = "absolute";
        dialog.style.inset = "0";
        dialog.style.background = "rgba(15,23,42,.35)";
        dialog.style.display = "flex";
        dialog.style.alignItems = "center";
        dialog.style.justifyContent = "center";
        dialog.style.zIndex = "3";

        var card = document.createElement("div");
        card.className = "card shadow-sm";
        card.style.width = "min(30rem, calc(100% - 2rem))";
        card.innerHTML = ""
          + "<div class='card-body d-flex flex-column gap-2'>"
          + "  <div class='small text-uppercase text-muted'>New Folder</div>"
          + "  <h6 class='mb-1'>Create Folder In " + esc(pickerState.currentFolder ? ("/" + pickerState.currentFolder) : "/") + "</h6>"
          + "  <p class='small text-muted mb-2'>Provide one folder name segment.</p>"
          + "  <input type='text' class='form-control form-control-sm' placeholder='Folder name' data-uri-folder-name='true'>"
          + "  <div class='d-flex justify-content-end gap-2 mt-2'>"
          + "    <button type='button' class='btn btn-outline-secondary btn-sm' data-uri-folder-cancel='true'>Cancel</button>"
          + "    <button type='button' class='btn btn-primary btn-sm' data-uri-folder-create='true'>Create</button>"
          + "  </div>"
          + "</div>";

        dialog.appendChild(card);
        overlay.querySelector(".ua-uri-picker-main").appendChild(dialog);

        var input = dialog.querySelector("[data-uri-folder-name]");
        var cancel = dialog.querySelector("[data-uri-folder-cancel]");
        var create = dialog.querySelector("[data-uri-folder-create]");

        function close(value) {
          if (dialog && dialog.parentNode) dialog.parentNode.removeChild(dialog);
          resolve(value || "");
        }

        cancel.addEventListener("click", function() { close(""); });
        create.addEventListener("click", function() { close(input.value || ""); });
        input.addEventListener("keydown", function(event) {
          if (event.key === "Enter") {
            event.preventDefault();
            close(input.value || "");
          }
          if (event.key === "Escape") {
            event.preventDefault();
            close("");
          }
        });
        input.focus();
      });
    })();

    folderName = normalizeFolderPath(folderName || "");
    if (!(folderName)) return;

    try {
      var path = pickerState.currentFolder
        ? normalizeFolderPath(pickerState.currentFolder + "/" + folderName)
        : folderName;

      var created = await postJson("/files/picker/folder", {
        scope: "content",
        path: path,
        title: folderLabel(path)
      });

      pickerState.currentFolder = normalizeFolderPath(created.path || path);
      await loadData();
      syncStatus("Folder created.", false);
    } catch (error) {
      syncStatus((error && error.message) || "Could not create folder.", true);
    }
  }

  overlay.querySelector("[data-uri-close]").addEventListener("click", close);
  overlay.querySelector("[data-uri-cancel]").addEventListener("click", close);
  applyBtn.addEventListener("click", function() {
    var values = selectedValues().map(function(uri) {
      return resolveProviderUri(uri);
    });
    if (!(multiselect)) values = values.length > 0 ? [values[0]] : [];

    state.values = values;
    assignState(control, values, multiselect, options);
    state.syncFromValues();
    close();
  });

  searchInput.addEventListener("input", function() {
    pickerState.search = safe(searchInput.value || "").trim();
    loadData();
  });

  kindInput.addEventListener("change", function() {
    pickerState.kindFilter = safe(kindInput.value || "").trim();
    loadData();
  });

  overlay.querySelectorAll("[data-uri-view]").forEach(function(btn) {
    btn.addEventListener("click", function() {
      overlay.querySelectorAll("[data-uri-view]").forEach(function(other) {
        other.classList.remove("active");
      });
      this.classList.add("active");
      pickerState.viewMode = safe(this.getAttribute("data-uri-view") || "list").trim();
      renderRows();
    });
  });

  newFolderBtn.addEventListener("click", function() {
    createFolder();
  });

  updateApplyState();
  await loadData();
}

exports.render = async function(options) {
  ensureStyles();

  var id = "uri-" + uai.generateRandomID(10);
  var multiselect = bool(options, "multiselect", false);
  var allowThirdParty = bool(options, "allowthirdparty", true);
  var allowUpload = bool(options, "allowupload", true);
  var values = parseValues(options.value || "", multiselect);

  var payload = {
    n: "div",
    c: "form-group pb-3",
    i: [
      { n: "label", c: "pb-2 fs-4", for: id, i: options.title || "URI" },
      {
        n: "div",
        c: "input-group",
        i: [
          {
            n: "input",
            type: "text",
            id: id,
            class: "form-control",
            name: options.name,
            value: multiselect ? values.join(", ") : (values[0] || ""),
            placeholder: multiselect
              ? "Add one or more URLs, or browse to select..."
              : "https://example.com/or/file/uri"
          },
          {
            n: "button",
            type: "button",
            class: "btn btn-outline-secondary",
            "data-uri-browse": "true",
            i: [{ n: "i", c: "bi bi-folder2-open me-1" }, "Browse"]
          }
        ]
      },
      {
        n: "div",
        c: "d-flex gap-2 mt-2",
        i: multiselect
          ? [{ n: "button", type: "button", class: "btn btn-outline-primary btn-sm", "data-uri-add": "true", i: [{ n: "i", c: "bi bi-plus-lg me-1" }, "Add URL"] }]
          : []
      },
      { n: "div", c: "ua-uri-selector-values", "data-uri-values": "true" },
      {
        n: "div",
        c: "form-text",
        i: "Use external URLs, internal /content/* and /secured/*, or provider URIs (dropbox://, gdrive://, s3://, ftp://)."
      }
    ]
  };

  var dom = await jsonRender.render(payload);
  var input = dom.querySelector("input");
  var browseBtn = dom.querySelector("[data-uri-browse]");
  var addBtn = dom.querySelector("[data-uri-add]");
  var valueHost = dom.querySelector("[data-uri-values]");

  var state = {
    multiselect: multiselect,
    allowThirdParty: allowThirdParty,
    allowUpload: allowUpload,
    expectedKinds: parseExpectedKinds(options.expectedkind || []),
    values: values,
    syncFromValues: function() {
      if (state.multiselect) {
        input.value = state.values.join(", ");
      } else {
        input.value = state.values.length > 0 ? state.values[0] : "";
      }

      assignState(dom.closest("uae") || dom, state.values, state.multiselect, options);
      renderValuePills(valueHost, state.values, function(uri) {
        state.values = state.values.filter(function(item) { return item !== uri; });
        state.syncFromValues();
        var uae = dom.closest("uae");
        if (uae && typeof save === "function") save(options.name, uae, {});
      });
    }
  };

  state.syncFromValues();

  browseBtn.addEventListener("click", async function() {
    await openPicker(dom.closest("uae") || dom, options, state);
    var uae = dom.closest("uae");
    if (uae && typeof save === "function") save(options.name, uae, {});
  });

  if (addBtn) {
    addBtn.addEventListener("click", function() {
      var raw = safe(input.value || "").trim();
      if (!(raw)) return;

      var next = parseValues(raw, true).map(function(item) {
        return resolveProviderUri(item);
      });

      for (var i = 0; i < next.length; i++) {
        var err = uriValidationError(next[i], options);
        if (err) {
          console.warn("ua.uri.selector: invalid URI ignored", { uri: next[i], reason: err });
          continue;
        }
        if (state.values.indexOf(next[i]) < 0) state.values.push(next[i]);
      }

      state.syncFromValues();
      var uae = dom.closest("uae");
      if (uae && typeof save === "function") save(options.name, uae, {});
    });
  }

  input.addEventListener("change", function() {
    if (multiselect) {
      return;
    }
    var normalized = resolveProviderUri(input.value || "");
    state.values = normalized ? [normalized] : [];
    state.syncFromValues();
    var uae = dom.closest("uae");
    if (uae && typeof save === "function") save(options.name, uae, {});
  });

  return dom;
};

async function save(name, control, repo) {
  var input = control.querySelector("input");
  var multiselect = control.querySelector("[data-uri-add]") !== null;
  var values = readState(control, input, multiselect).map(function(uri) {
    return resolveProviderUri(uri);
  }).filter(Boolean);

  var definition = {};
  if (control && control._ua && control._ua.options) {
    definition = control._ua.options;
  } else if (control && control._uaUriOptions) {
    definition = control._uaUriOptions;
  }

  repo.errors = [];
  for (var i = 0; i < values.length; i++) {
    var error = uriValidationError(values[i], definition || {});
    if (error) {
      repo.errors.push({
        type: "validation",
        message: error,
        input: {
          id: input ? input.id : "",
          name: name
        }
      });
    }
  }

  if ((definition && definition.required === true) && values.length === 0) {
    repo.errors.push({
      type: "validation",
      message: "A URI is required.",
      input: {
        id: input ? input.id : "",
        name: name
      }
    });
  }

  if (repo.errors.length > 0) {
    repo.success = false;
    control.classList.add("is-invalid");
    control.classList.remove("is-valid");
  } else {
    repo.success = true;
    control.classList.remove("is-invalid");
    control.classList.add("is-valid");
  }

  repo.data = multiselect ? values : (values[0] || "");
  return repo;
}
module.exports.save = save;

async function load(name, control, data) {
  var input = control.querySelector("input");
  if (!(input)) return;

  var multiselect = control.querySelector("[data-uri-add]") !== null;
  var values = parseValues(data, multiselect).map(function(uri) {
    return resolveProviderUri(uri);
  }).filter(Boolean);

  assignState(control, values, multiselect, control._uaUriOptions || {});

  if (multiselect) {
    input.value = values.join(", ");
  } else {
    input.value = values.length > 0 ? values[0] : "";
  }

  var valueHost = control.querySelector("[data-uri-values]");
  if (valueHost) {
    renderValuePills(valueHost, values, function(uri) {
      var next = values.filter(function(item) { return item !== uri; });
      assignState(control, next, multiselect, control._uaUriOptions || {});
      input.value = multiselect ? next.join(", ") : (next[0] || "");
      renderValuePills(valueHost, next, function() {});
    });
  }
}
module.exports.load = load;
